import React, { useMemo, useState, useEffect } from 'react';
import { Alert, Badge, Button, Form, Tab, Tabs } from 'react-bootstrap';
import {
  ACCESSIBLE_PRESETS,
  DEFAULT_THEME_COLORS,
  UI_COLOR_TOKENS,
  useTheme,
} from '../contexts/ThemeContext';

const isHexColor = (value) => /^#[0-9A-Fa-f]{6}$/.test(value || '');

const hexToRgb = (hex) => {
  if (!isHexColor(hex)) return null;
  const value = hex.slice(1);
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
};

const relativeLuminance = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const channel = (value) => {
    const next = value / 255;
    return next <= 0.03928 ? next / 12.92 : ((next + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
};

const contrastRatio = (foreground, background) => {
  const fg = relativeLuminance(foreground);
  const bg = relativeLuminance(background);
  if (fg === null || bg === null) return null;
  const lighter = Math.max(fg, bg);
  const darker = Math.min(fg, bg);
  return (lighter + 0.05) / (darker + 0.05);
};

function ColorControl({ token, value, defaultValue, onChange, onReset }) {
  const [draft, setDraft] = useState(value);
  const changed = value.toUpperCase() !== defaultValue.toUpperCase();

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const updateDraft = (next) => {
    const normalized = next.startsWith('#') ? next : `#${next}`;
    setDraft(normalized);
    if (isHexColor(normalized)) {
      onChange(normalized.toUpperCase());
    }
  };

  return (
    <div className="ui-color-row">
      <div className="ui-color-meta">
        <div className="ui-color-label-row">
          <span className="ui-color-label">{token.label}</span>
          <code>{token.cssVar}</code>
        </div>
        <p>{token.description}</p>
      </div>
      <div className="ui-color-controls">
        <input
          type="color"
          className="ui-color-picker"
          value={isHexColor(value) ? value : defaultValue}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          aria-label={`${token.label} color picker`}
        />
        <Form.Control
          size="sm"
          value={draft}
          spellCheck="false"
          className={isHexColor(draft) ? '' : 'is-invalid'}
          onChange={(event) => updateDraft(event.target.value.trim())}
          onBlur={() => {
            if (!isHexColor(draft)) setDraft(value);
          }}
          aria-label={`${token.label} hex color`}
        />
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={onReset}
          disabled={!changed}
          title="Reset this color"
        >
          <i className="fas fa-rotate-left"></i>
        </Button>
      </div>
    </div>
  );
}

function ContrastCard({ label, foreground, background }) {
  const ratio = contrastRatio(foreground, background);
  const score = ratio ? ratio.toFixed(2) : 'N/A';
  const passesAA = ratio !== null && ratio >= 4.5;
  const passesLarge = ratio !== null && ratio >= 3;

  return (
    <div className={`contrast-card ${passesAA ? 'pass' : passesLarge ? 'warn' : 'fail'}`}>
      <div className="contrast-swatch" style={{ background, color: foreground }}>
        Aa
      </div>
      <div>
        <strong>{label}</strong>
        <span>{score}:1</span>
      </div>
      <Badge bg={passesAA ? 'success' : passesLarge ? 'warning' : 'danger'} text={passesLarge && !passesAA ? 'dark' : undefined}>
        {passesAA ? 'AA' : passesLarge ? 'Large' : 'Fail'}
      </Badge>
    </div>
  );
}

function PreviewPanel({ themeKey, getColor }) {
  return (
    <div className="ui-preview-panel">
      <div className="ui-preview-card" style={{ background: getColor(themeKey, '--bg-secondary'), borderColor: getColor(themeKey, '--border-color') }}>
        <div>
          <h5 style={{ color: getColor(themeKey, '--text-primary') }}>Preview Surface</h5>
          <p style={{ color: getColor(themeKey, '--text-secondary') }}>
            Secondary text should stay readable during long sessions.
          </p>
          <small style={{ color: getColor(themeKey, '--text-muted') }}>Muted copy, timestamps, and helper labels.</small>
        </div>
        <button
          type="button"
          className="ui-preview-button"
          style={{ background: getColor(themeKey, '--ut-orange'), color: '#fff' }}
        >
          Primary Action
        </button>
      </div>
      <div className="ui-preview-chat" style={{ background: getColor(themeKey, '--chat-bg') }}>
        <div className="ui-preview-bubble bot" style={{ background: getColor(themeKey, '--chat-bubble-bot'), color: getColor(themeKey, '--text-primary'), borderColor: getColor(themeKey, '--border-color') }}>
          Assistant answer text should be crisp and calm.
        </div>
        <div className="ui-preview-bubble user" style={{ background: getColor(themeKey, '--ut-orange'), color: '#fff' }}>
          User message
        </div>
      </div>
    </div>
  );
}

function UIControlsTab() {
  const {
    isDarkMode,
    setDarkMode,
    themeKey,
    uiPreferences,
    setUiPreference,
    resetUiPreferences,
    setColorToken,
    resetColorToken,
    resetThemeColors,
    resetAllColors,
    applyColorPreset,
    getEffectiveColor,
    exportUiSettings,
    importUiSettings,
  } = useTheme();

  const [editingTheme, setEditingTheme] = useState(themeKey);
  const [importText, setImportText] = useState('');
  const [importMessage, setImportMessage] = useState(null);

  useEffect(() => {
    setEditingTheme(themeKey);
  }, [themeKey]);

  const contrastPairs = useMemo(() => ([
    ['Primary text on page', '--text-primary', '--bg-primary'],
    ['Secondary text on page', '--text-secondary', '--bg-primary'],
    ['Muted text on page', '--text-muted', '--bg-primary'],
    ['Primary text on panel', '--text-primary', '--bg-secondary'],
    ['Primary text on assistant bubble', '--text-primary', '--chat-bubble-bot'],
    ['Link blue on panel', '--ut-blue', '--bg-secondary'],
  ]), []);

  const exportedSettings = useMemo(() => JSON.stringify(exportUiSettings(), null, 2), [exportUiSettings]);

  const handleImport = () => {
    try {
      const parsed = JSON.parse(importText);
      const ok = importUiSettings(parsed);
      setImportMessage(ok ? { variant: 'success', text: 'UI settings imported.' } : { variant: 'danger', text: 'Could not import those settings.' });
    } catch {
      setImportMessage({ variant: 'danger', text: 'Invalid JSON. Paste a settings export and try again.' });
    }
  };

  const copyExport = async () => {
    try {
      await navigator.clipboard.writeText(exportedSettings);
      setImportMessage({ variant: 'success', text: 'Settings copied to clipboard.' });
    } catch {
      setImportMessage({ variant: 'warning', text: 'Clipboard copy was blocked. Select and copy the JSON manually.' });
    }
  };

  return (
    <div className="ui-controls-tab">
      <div className="ui-controls-hero">
        <div>
          <p className="ui-eyebrow">Interface tuning</p>
          <h2>UI Controls</h2>
          <p>Adjust color, contrast, typography, and comfort settings for this browser. Changes apply live and are saved locally.</p>
        </div>
        <div className="ui-mode-toggle" role="group" aria-label="Theme mode">
          <button type="button" className={!isDarkMode ? 'active' : ''} onClick={() => setDarkMode(false)}>
            <i className="fas fa-sun"></i> Light
          </button>
          <button type="button" className={isDarkMode ? 'active' : ''} onClick={() => setDarkMode(true)}>
            <i className="fas fa-moon"></i> Dark
          </button>
        </div>
      </div>

      <Tabs defaultActiveKey="colors" className="ui-control-tabs">
        <Tab eventKey="colors" title={<><i className="fas fa-palette me-2"></i>Colors</>}>
          <div className="ui-control-section">
            <div className="ui-toolbar-row">
              <div className="ui-segmented" role="group" aria-label="Palette editor">
                <button type="button" className={editingTheme === 'light' ? 'active' : ''} onClick={() => setEditingTheme('light')}>Light palette</button>
                <button type="button" className={editingTheme === 'dark' ? 'active' : ''} onClick={() => setEditingTheme('dark')}>Dark palette</button>
              </div>
              <div className="ui-toolbar-actions">
                <Button size="sm" variant="ut-primary" onClick={() => applyColorPreset(editingTheme, ACCESSIBLE_PRESETS[editingTheme])}>
                  <i className="fas fa-universal-access me-2"></i>{ACCESSIBLE_PRESETS[editingTheme]?.name || 'Accessible preset'}
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => resetThemeColors(editingTheme)}>
                  Reset {editingTheme}
                </Button>
                <Button size="sm" variant="outline-danger" onClick={resetAllColors}>
                  Reset all
                </Button>
              </div>
            </div>

            <div className="ui-controls-grid">
              <div className="ui-color-groups">
                {UI_COLOR_TOKENS.map(group => (
                  <section className="ui-control-card" key={group.group}>
                    <h4>{group.group}</h4>
                    {group.tokens.map(token => (
                      <ColorControl
                        key={token.cssVar}
                        token={token}
                        value={getEffectiveColor(editingTheme, token.cssVar)}
                        defaultValue={DEFAULT_THEME_COLORS[editingTheme][token.cssVar] || '#000000'}
                        onChange={(value) => setColorToken(editingTheme, token.cssVar, value)}
                        onReset={() => resetColorToken(editingTheme, token.cssVar)}
                      />
                    ))}
                  </section>
                ))}
              </div>

              <aside className="ui-side-panel">
                <section className="ui-control-card sticky-preview">
                  <h4>Live Preview</h4>
                  <PreviewPanel themeKey={editingTheme} getColor={getEffectiveColor} />
                </section>

                <section className="ui-control-card">
                  <h4>Contrast Check</h4>
                  <div className="contrast-list">
                    {contrastPairs.map(([label, fg, bg]) => (
                      <ContrastCard
                        key={label}
                        label={label}
                        foreground={getEffectiveColor(editingTheme, fg)}
                        background={getEffectiveColor(editingTheme, bg)}
                      />
                    ))}
                  </div>
                </section>
              </aside>
            </div>
          </div>
        </Tab>

        <Tab eventKey="comfort" title={<><i className="fas fa-sliders me-2"></i>Comfort</>}>
          <div className="ui-control-section two-column-controls">
            <section className="ui-control-card">
              <h4>Reading Comfort</h4>
              <Form.Group className="mb-4">
                <Form.Label>Font scale: {Math.round(uiPreferences.fontScale * 100)}%</Form.Label>
                <Form.Range
                  min="0.9"
                  max="1.25"
                  step="0.01"
                  value={uiPreferences.fontScale}
                  onChange={(event) => setUiPreference('fontScale', Number(event.target.value))}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Interface density</Form.Label>
                <div className="ui-segmented wide">
                  {[
                    ['compact', 'Compact'],
                    ['comfortable', 'Comfortable'],
                    ['spacious', 'Spacious'],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={uiPreferences.density === value ? 'active' : ''}
                      onClick={() => setUiPreference('density', value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </Form.Group>

              <Form.Check
                type="switch"
                id="high-contrast-text"
                label="Boost text contrast across dark panels"
                checked={uiPreferences.highContrastText}
                onChange={(event) => setUiPreference('highContrastText', event.target.checked)}
              />
              <Form.Check
                type="switch"
                id="reduce-background-effects"
                label="Reduce decorative background effects"
                checked={uiPreferences.reduceBackgroundEffects}
                onChange={(event) => setUiPreference('reduceBackgroundEffects', event.target.checked)}
                className="mt-3"
              />

              <Button variant="outline-secondary" className="mt-4" onClick={resetUiPreferences}>
                Reset comfort settings
              </Button>
            </section>

            <section className="ui-control-card">
              <h4>Helpful Defaults</h4>
              <div className="quick-actions-list">
                <button type="button" onClick={() => { setDarkMode(true); applyColorPreset('dark', ACCESSIBLE_PRESETS.dark); setUiPreference('highContrastText', true); }}>
                  <i className="fas fa-eye"></i>
                  <span>
                    <strong>Dark readability preset</strong>
                    <small>Brighter text, clearer borders, calmer panels.</small>
                  </span>
                </button>
                <button type="button" onClick={() => { setUiPreference('fontScale', 1.08); setUiPreference('density', 'comfortable'); }}>
                  <i className="fas fa-text-height"></i>
                  <span>
                    <strong>Long-session reading</strong>
                    <small>Slightly larger type without making the interface huge.</small>
                  </span>
                </button>
                <button type="button" onClick={() => { setUiPreference('reduceBackgroundEffects', true); }}>
                  <i className="fas fa-layer-group"></i>
                  <span>
                    <strong>Quiet background</strong>
                    <small>Reduces patterns and ambient effects for focus.</small>
                  </span>
                </button>
              </div>
            </section>
          </div>
        </Tab>

        <Tab eventKey="portability" title={<><i className="fas fa-file-export me-2"></i>Import / Export</>}>
          <div className="ui-control-section two-column-controls">
            <section className="ui-control-card">
              <h4>Export Settings</h4>
              <p className="ui-muted-copy">Save or share your current UI palette and comfort settings.</p>
              <Form.Control as="textarea" rows={12} readOnly value={exportedSettings} className="ui-json-box" />
              <Button variant="ut-primary" className="mt-3" onClick={copyExport}>
                <i className="fas fa-copy me-2"></i>Copy JSON
              </Button>
            </section>

            <section className="ui-control-card">
              <h4>Import Settings</h4>
              <p className="ui-muted-copy">Paste a previous export. Invalid or unknown color tokens are ignored.</p>
              {importMessage && <Alert variant={importMessage.variant}>{importMessage.text}</Alert>}
              <Form.Control
                as="textarea"
                rows={12}
                value={importText}
                onChange={(event) => setImportText(event.target.value)}
                placeholder="Paste UI settings JSON here"
                className="ui-json-box"
              />
              <Button variant="ut-primary" className="mt-3" onClick={handleImport} disabled={!importText.trim()}>
                <i className="fas fa-file-import me-2"></i>Import JSON
              </Button>
            </section>
          </div>
        </Tab>
      </Tabs>
    </div>
  );
}

export default UIControlsTab;
